import {
  DocumentTemplateType,
  QueryFactory,
  TemplateDto,
} from '@lmt/shared-types';
import { RibbonTabType } from '@lmt/unicorn-shared/app/common/devexpress/RichEdit/dx-richedit';
import { getTemplateClient } from '@lmt/unicorn-shared/app/components/documents/richedit/SaveTemplateRichEditButton';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { Buffer } from 'buffer';
import { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useFilePicker } from 'use-file-picker';
import { FileTypeValidator } from 'use-file-picker/validators';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { useEventCallback } from '@mui/material';

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  currentTemplate: TemplateDto | null;
  templateType: DocumentTemplateType;
  tab: RibbonTabType;
};
/*
  Adds "Export Template" and "Import Template" menu item to RichEdit and handles it
 */
export const ImportExportTemplateButton: React.FC<Props> = (props) => {
  const { richEdit } = props;

  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('documents');

  const importTemplateMenuId = 'importTemplateMenuId';
  const exportTemplateMenuId = 'exportTemplateMenuId';

  const { openFilePicker } = useFilePicker({
    readAs: 'ArrayBuffer',
    multiple: true,
    accept: ['.rtf', '.doc', '.docx'],
    validators: [new FileTypeValidator(['rtf', 'doc', 'docx'])],
    async onFilesSuccessfullySelected(data) {
      const prefix = await modals.showPrompt({
        title: i18n.t('prefix_prompt.title'),
        text: i18n.t('prefix_prompt.text'),
        defaultValue: '',
      });

      const promises = [];
      const templateClient = getTemplateClient(props.templateType);

      if (prefix != null) {
        for (const fileContent of data.filesContent) {
          let title = (prefix + ' ' + fileContent.name).trim();

          // Otherwise importing RTF files do not work (via Import)
          // See #47156: RTF file is corrupt and cannot be opened
          title = title.replace(/\.[^/.]+$/, '');

          promises.push(
            templateClient.create({
              title: title,
              content: Buffer.from(fileContent.content).toString('base64'),
              type: props.templateType,
            }),
          );
        }
      }

      const values = await Promise.allSettled(promises);
      const successCount = values.filter(
        (v) => v.status === 'fulfilled',
      ).length;

      if (successCount) {
        toast(
          i18n.t('toast_success', {
            count: successCount,
          }),
          {
            type: 'success',
          },
        );
      }
      if (values.length !== successCount) {
        toast(
          i18n.t('toast_error', {
            count: values.length - successCount,
          }),
          {
            type: 'warning',
          },
        );
      }

      switch (props.templateType) {
        case DocumentTemplateType.HL7OutgoingMessage:
          return await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7OutgoingTemplatesQuery.getQueryKey(),
          });
        case DocumentTemplateType.GDTOutgoingMessage:
          return await queryClient.invalidateQueries({
            queryKey: QueryFactory.GDTOutgoingTemplatesQuery.getQueryKey(),
          });
        case DocumentTemplateType.DocumentFooter:
        case DocumentTemplateType.DocumentHeader:
        case DocumentTemplateType.Document:
          return await queryClient.invalidateQueries({
            queryKey: QueryFactory.DocumentTemplateQuery.getQueryKey(),
          });

        default:
          assertNever(props.templateType);
      }
    },
    async onFilesRejected() {
      await modals.showError({ text: i18n.t('file_type_error') });
    },
  });

  const importExportCustomCommandExecutedEventHandler = useEventCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }
      if (args.commandName === exportTemplateMenuId) {
        richEdit.current.downloadDocument(DXR.DocumentFormat.OpenXml);
      }

      if (args.commandName === importTemplateMenuId) {
        openFilePicker();
      }
    },
  );

  useEffect(
    function AddImportExportButtons() {
      richEdit.current.updateRibbon((ribbon) => {
        if (!ribbon) return;
        const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
        newFilePanel.insertItem(
          new DXR.RibbonButtonItem(
            importTemplateMenuId,
            i18n.t('import_template_button'),
            {
              showText: true,
            },
          ),
        );
        newFilePanel.insertItem(
          new DXR.RibbonButtonItem(
            exportTemplateMenuId,
            i18n.t('export_template_button'),
            {
              showText: true,
            },
          ),
        );
      });
      richEdit.current.events.customCommandExecuted.addHandler(
        importExportCustomCommandExecutedEventHandler,
      );

      return () => {
        richEdit.current.events.customCommandExecuted.removeHandler(
          importExportCustomCommandExecutedEventHandler,
        );

        if (richEdit.current?.isDisposed === false) {
          richEdit.current.updateRibbon((ribbon) => {
            if (!ribbon) return;

            const filePanel = ribbon.getTab(DXR.RibbonTabType.File);
            if (!filePanel) return;

            filePanel.removeItem(importTemplateMenuId);
            filePanel.removeItem(exportTemplateMenuId);
          });
        }
      };
    },
    [importExportCustomCommandExecutedEventHandler, richEdit],
  );

  return null;
};
